import { VisuallyHidden } from "@mantine/core";
import { IconCheck, IconCloudOff } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { getStatus, syncNow } from "../sync/engine";
import "./PullToRefresh.css";

// Pull this far (px, after damping) and let go to refresh.
const THRESHOLD = 72;
// The pull eases off towards this, like stretching a rubber band.
const MAX_PULL = 140;
// Where the content rests while the refresh runs.
const HOLD = 60;
// Movement before deciding whether the finger is pulling or doing something else.
const DECIDE_AFTER = 8;
// Spin at least this long, so a fast refresh still reads as one.
const MIN_SPIN_MS = 650;
// How long the tick (or offline icon) shows before everything slides back.
const DONE_MS = 550;

const RING_R = 9;
const RING_C = 2 * Math.PI * RING_R;

type Phase = "idle" | "pulling" | "armed" | "refreshing" | "done" | "offline";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const damp = (dy: number) => MAX_PULL * (1 - Math.exp(-dy / MAX_PULL));

/** Whether the touch began inside something that is itself scrolled down (a list, a table). */
function insideScrolled(target: EventTarget | null, root: HTMLElement): boolean {
  for (let el = target as HTMLElement | null; el && el !== root; el = el.parentElement) {
    if (el.scrollTop > 0) return true;
  }
  return false;
}

/**
 * Pull the page down from the top to refetch what's on screen and send pending changes.
 * Touch screens only. Also stops Chrome's own pull-to-refresh, which reloads the whole app.
 */
export function PullToRefresh({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const root = useRef<HTMLDivElement>(null);
  const [pull, setPull] = useState(0);
  const [phase, setPhase] = useState<Phase>("idle");

  useEffect(() => {
    const el = root.current;
    if (!el) return;
    let start: { x: number; y: number } | null = null;
    let mode: "ignore" | "unknown" | "pull" = "ignore";
    let armed = false;
    let busy = false;

    const reset = () => {
      mode = "ignore";
      start = null;
      armed = false;
      setPhase("idle");
      setPull(0);
    };

    const refresh = async () => {
      busy = true;
      setPhase("refreshing");
      setPull(HOLD);
      await Promise.all([
        queryClient.refetchQueries({ type: "active" }),
        syncNow(),
        sleep(MIN_SPIN_MS),
      ]).catch(() => {});
      setPhase(getStatus().connection === "offline" ? "offline" : "done");
      await sleep(DONE_MS);
      setPhase("idle");
      setPull(0);
      busy = false;
    };

    const onStart = (e: TouchEvent) => {
      mode = "ignore";
      const t = e.touches[0];
      if (!t || busy || e.touches.length !== 1 || window.scrollY > 0 || insideScrolled(e.target, el)) return;
      start = { x: t.clientX, y: t.clientY };
      mode = "unknown";
      armed = false;
    };

    const onMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (mode === "ignore" || !start || !t) return;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      if (mode === "unknown") {
        // A downward drag at the top would only bounce the page, so claim it straight away:
        // iOS won't let a gesture be taken over once it has started scrolling.
        const downward = dy > 0 && dy >= Math.abs(dx);
        if (downward && e.cancelable) e.preventDefault();
        if (Math.abs(dx) < DECIDE_AFTER && Math.abs(dy) < DECIDE_AFTER) return;
        if (!downward || window.scrollY > 0) {
          mode = "ignore";
          return;
        }
        mode = "pull";
      }
      if (e.cancelable) e.preventDefault();
      const distance = damp(Math.max(0, dy - DECIDE_AFTER));
      setPull(distance);
      const nowArmed = distance >= THRESHOLD;
      if (nowArmed && !armed) navigator.vibrate?.(10);
      armed = nowArmed;
      setPhase(nowArmed ? "armed" : "pulling");
    };

    const onEnd = () => {
      if (mode !== "pull") {
        mode = "ignore";
        return;
      }
      mode = "ignore";
      if (armed) void refresh();
      else reset();
    };

    const onCancel = () => {
      if (mode === "pull") reset();
      mode = "ignore";
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onCancel);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onCancel);
    };
  }, [queryClient]);

  const dragging = phase === "pulling" || phase === "armed";
  const progress = Math.min(pull / THRESHOLD, 1);
  // The ring winds round as it fills, then keeps turning a little past the threshold.
  const turn = dragging ? pull * 2.4 : 0;

  return (
    <div ref={root} className="ptr" data-phase={phase} data-testid="pull-to-refresh">
      <div
        aria-hidden
        className="ptr-indicator"
        style={{
          transform: `translate(-50%, ${pull - 64}px)`,
          opacity: phase === "idle" ? 0 : dragging ? Math.min(1, progress * 1.6) : 1,
        }}
      >
        <div className="ptr-bubble" style={{ transform: `scale(${dragging ? 0.6 + 0.4 * progress : 1})` }}>
          <svg className="ptr-ring" viewBox="0 0 24 24" style={{ transform: `rotate(${turn - 90}deg)` }}>
            <circle className="ptr-track" cx="12" cy="12" r={RING_R} />
            <circle
              className="ptr-arc"
              cx="12"
              cy="12"
              r={RING_R}
              strokeDasharray={`${(phase === "refreshing" ? 0.7 : progress) * RING_C} ${RING_C}`}
            />
          </svg>
          {phase === "done" && <IconCheck className="ptr-icon" size={20} stroke={3} />}
          {phase === "offline" && <IconCloudOff className="ptr-icon" size={18} stroke={2.2} />}
        </div>
      </div>
      <VisuallyHidden role="status">
        {phase === "refreshing" ? "Refreshing" : phase === "done" ? "Up to date" : phase === "offline" ? "Offline, showing saved data" : ""}
      </VisuallyHidden>
      <div className="ptr-content" style={pull > 0 ? { transform: `translateY(${pull}px)` } : undefined}>
        {children}
      </div>
    </div>
  );
}
